import { inject, Injectable } from "@angular/core";
import { AssetHierarchyDTO } from "../../../models/api/asset/assetHierarchyDTO";
import { BehaviorSubject, forkJoin, map, Observable } from "rxjs";
import { AuthService } from "../auth/auth.service";
import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { USER_ROLES } from "../../../enums/userRoles.enum";
import { ASSET_TYPES } from "../../../enums/assetTypes.enum";

@Injectable({
  providedIn: "root",
})
export class AssetHierarchyService {
  private auth = inject(AuthService);
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);
  public assetHierarchy!: AssetHierarchyDTO[];
  public shouldRefreshAssetHierarchy = new BehaviorSubject(false);

  /**
   * Retrieves the asset hierarchy with the given root asset type and max level.
   * @param rootAssetType the type of the root asset
   * @param maxLevel the maximum level of the hierarchy to retrieve
   * @param rootAssetId the id of the root asset (optional)
   * @returns an observable of the asset hierarchy
   */
  public getAssetHierarchy(
    rootAssetType: string,
    maxLevel: number,
    rootAssetId?: string
  ): Observable<AssetHierarchyDTO[] | null> {
    return forkJoin({
      authUser: this.auth.getAuthenticatedUser(),
      assetHierarchy: this.fetchAssetHierarchy(rootAssetType, maxLevel, rootAssetId),
    }).pipe(
      map((res) => {
        // Manipulate response if role is tenant admin because we need tenant to be highest level of asset hierarchy:
        // Temporary fix for asset hierarchy.
        // Debt list (Technische Schuldenliste) has this as optimization in BE to deliver Tenant as highest hierarchy element
        if (this.auth.getRole() === USER_ROLES.TENANT_ADMIN) {
          const { tenantLabel, tenantId } = {
            tenantLabel: res.authUser?.tenant?.label,
            tenantId: res.authUser?.tenant?.id,
          };
          if (tenantLabel) {
            const assetHierarchy: AssetHierarchyDTO[] = [
              {
                children: res.assetHierarchy,
                label: tenantLabel,
                id: tenantId ? tenantId : "",
                assetType: ASSET_TYPES.TENANT,
              },
            ];
            return assetHierarchy;
          } else {
            return res.assetHierarchy;
          }
        } else {
          return res.assetHierarchy;
        }
      })
    );
  }

  public refreshAssetHierarchy() {
    this.shouldRefreshAssetHierarchy.next(true);
  }

  /**
   * Fetches the asset hierarchy for the given root asset type and max level.
   *
   * @param rootAssetType The type of the root asset.
   * @param maxLevel The maximum level of the asset hierarchy. If not provided, the full hierarchy will be fetched.
   * @param rootAssetId The id of the root asset. If not provided, the hierarchy will be fetched for the current user's tenant.
   * @returns An observable that emits the fetched asset hierarchy.
   */
  private fetchAssetHierarchy(rootAssetType: string, maxLevel: number, rootAssetId?: string) {
    let params: HttpParams = new HttpParams();
    if (rootAssetType) {
      params = params.set("rootAssetType", rootAssetType);
    }
    if (rootAssetId) {
      params = params.set("rootAssetId", rootAssetId);
    }

    if (maxLevel !== undefined && maxLevel !== null) {
      params = params.set("maxLevel", maxLevel.toString());
    }

    return this.http
      .get<AssetHierarchyDTO[]>(
        environment.baseUrl + "/assetHierarchy",

        { observe: "response", params }
      )
      .pipe(
        this.apiErrorHandler.handle(
          "asset_hierarchy",
          new HttpResponse({
            body: [],
          })
        ),
        map((res: HttpResponse<AssetHierarchyDTO[]>) => {
          if (res.status === 200 && res.body) {
            this.assetHierarchy = res.body;
            const orderedHierarchy = this.orderAssetHierarchy(res.body);
            return orderedHierarchy;
          } else {
            return [];
          }
        })
      );
  }

  /**
   * Orders the given asset hierarchy alphabetically by label. If the labels are missing, the order is not changed.
   * This function recursively orders the children of each asset as well.
   * @param assetHierarchy The asset hierarchy to order.
   * @returns The ordered asset hierarchy.
   */
  private orderAssetHierarchy(assetHierarchy: AssetHierarchyDTO[]) {
    assetHierarchy.sort((a, b) => {
      if (a.label && b.label) {
        return a.label.localeCompare(b.label);
      } else {
        return 0;
      }
    });

    for (const asset of assetHierarchy) {
      if (asset.children && asset.children.length > 0) {
        asset.children = this.orderAssetHierarchy(asset.children);
      }
    }

    return assetHierarchy;
  }
}
